import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon, PencilSquareIcon } from "@heroicons/react/24/outline";

import Card from "../shared/Card/Card";
import ConfirmModal from "../shared/Confirm/ConfirmModal";
import LoadingWheel from "../shared/LoadingWheel";
import NotesOutput from "../shared/Notes/NotesOutput";
import PartGradeBadge from "../shared/PartGradeBadge";
import useNotification from "../notifications/useNotifications";
import usePartMutations from "../../data/usePartMutations";
import useFlowTypeAccounts from "../../data/useFlowTypeAccounts";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useJobsByVehicle from "../../data/useJobsByVehicle";
import Modal from "../../layouts/Modal";
import { CheckFeatureFlag } from "../../hooks/useFeatureFlag";
import { AccountType } from "../../models/account/IAccount";
import { ResourceType } from "../../models/Note";
import { IPart, PartStatus } from "../../models/Part";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";

type DepollutionPartsEditorProps = {
  vehicle: IVehicle;
  /** Shows a per-part button to mark the part as damaged. */
  allowMarkDamaged?: boolean;
  /** Only lists parts assigned to a non-recycling flow account. */
  nonRecyclingOnly?: boolean;
};

const getPartStatusText = (status: PartStatus | null | undefined) => {
  if (status === null || status === undefined) return "-";
  return PartStatus[status];
};

export default function DepollutionPartsEditor({
  vehicle,
  allowMarkDamaged = false,
  nonRecyclingOnly = false,
}: DepollutionPartsEditorProps) {
  const { t } = useTranslation("vehicles");
  const isTMEEnabled = CheckFeatureFlag("TME");
  const { patchPart, markDamaged } = usePartMutations();
  const { jobs, createWithPart } = useJobsByVehicle(vehicle.id);
  const { flowTypeAccounts } = useFlowTypeAccounts("DepollutionPartsEditor");
  const { saveHandlers } = useDefaultCRUDHandlers(t("Part"));
  const { saveHandlers: jobSaveHandlers } = useDefaultCRUDHandlers(t("Job"));
  const { addNotification } = useNotification();
  const [savingAccountForPartId, setSavingAccountForPartId] = useState<string | null>(null);
  const [attachingPartId, setAttachingPartId] = useState<string | null>(null);
  const [selectedJobByPartId, setSelectedJobByPartId] = useState<Record<string, string>>({});
  const [notesPart, setNotesPart] = useState<IPart | null>(null);
  const [damagedPart, setDamagedPart] = useState<IPart | null>(null);
  const [damageNote, setDamageNote] = useState("");

  // Assigned to a non-recycling (customer) flow account. False until the accounts have loaded.
  const isNonRecycleAccount = (part: IPart) => {
    if (!part.accountId || !flowTypeAccounts.data?.data) return false;
    const assignedAccount = flowTypeAccounts.data.data.find((account) => account.id === part.accountId);
    return assignedAccount?.accountType !== AccountType.Recycling;
  };

  const parts = (vehicle.parts ?? [])
    .filter((part): part is IPart => !!part)
    .filter((part) => !nonRecyclingOnly || isNonRecycleAccount(part));

  const accountOptions = useMemo(
    () => (flowTypeAccounts.data?.data ?? []).map((account) => ({ id: account.id, name: account.name })),
    [flowTypeAccounts.data?.data]
  );
  const jobsForVehicle = jobs.data?.data ?? [];

  const attachedJobByPartId = useMemo(() => {
    const nextMap: Record<string, string> = {};
    for (const job of jobsForVehicle) {
      for (const jobPartCategory of job.jobPartCategories ?? []) {
        if (jobPartCategory.partDataId) {
          nextMap[jobPartCategory.partDataId] = job.id;
        }
      }
    }
    return nextMap;
  }, [jobsForVehicle]);

  const jobOptions = useMemo(
    () => jobsForVehicle.map((job) => ({ id: job.id, name: job.title })),
    [jobsForVehicle]
  );

  const handleAccountChange = (part: IPart, nextAccountId: string) => {
    const accountId = nextAccountId || null;
    if ((part.accountId ?? null) === accountId) return;

    setSavingAccountForPartId(part.id);
    patchPart.mutate(
      {
        id: part.id,
        vehicleId: part.vehicleId,
        accountId,
        updateAccount: true,
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
        },
        onError: () => {
          saveHandlers.onError();
          addNotification({
            variant: "error",
            primaryText: t("Unable to update account"),
          });
        },
        onSettled: () => {
          setSavingAccountForPartId(null);
        },
      }
    );
  };

  const handleAttachToJob = (part: IPart) => {
    const selectedJobId = selectedJobByPartId[part.id] ?? "";
    const existingAttachedJobId = attachedJobByPartId[part.id] ?? "";
    if (selectedJobId && selectedJobId === existingAttachedJobId) {
      addNotification({
        variant: "warn",
        primaryText: t("Part already attached to selected job"),
      });
      return;
    }

    setAttachingPartId(part.id);
    createWithPart.mutate(
      {
        partId: part.id,
        jobId: selectedJobId || null,
      },
      {
        onSuccess: () => {
          jobSaveHandlers.onSuccess();
        },
        onError: () => {
          jobSaveHandlers.onError();
          addNotification({
            variant: "error",
            primaryText: t("Unable to attach part to job"),
          });
        },
        onSettled: () => {
          setAttachingPartId(null);
        },
      }
    );
  };

  const openMarkDamaged = (part: IPart) => {
    setDamageNote("");
    setDamagedPart(part);
  };

  const handleMarkDamaged = () => {
    if (!damagedPart || !damageNote.trim()) return;

    markDamaged.mutate(
      {
        id: damagedPart.id,
        vehicleId: damagedPart.vehicleId,
        isDamaged: true,
        partStatus: PartStatus.Damaged,
        damageMessage: damageNote.trim(),
      },
      {
        onSuccess: () => {
          saveHandlers.onSuccess();
          setDamagedPart(null);
          setDamageNote("");
        },
        onError: () => {
          saveHandlers.onError();
          addNotification({
            variant: "error",
            primaryText: t("Unable to mark part as damaged"),
          });
        },
      }
    );
  };

  const isLoadingAccounts = nonRecyclingOnly && flowTypeAccounts.isLoading;

  return (
    <Card title={t("Vehicle Parts")} bodyClassName="p-4">
      <ConfirmModal
        open={!!damagedPart}
        setOpen={(open: boolean) => !open && setDamagedPart(null)}
        title={t("Mark part as damaged")}
        message={t("Mark {{title}} (tag {{tag}}) as damaged?", { title: damagedPart?.title ?? t("this part"), tag: damagedPart?.tagNumber ?? "-" })}
        confirmButtonText={t("Mark Damaged")}
        onConfirm={handleMarkDamaged}
        isLoading={markDamaged.isLoading}
        confirmDisabled={!damageNote.trim()}
      >
        <label htmlFor="damage-note" className="mt-3 block text-sm font-medium text-gray-700">
          {t("Damage note")}
        </label>
        <textarea
          id="damage-note"
          rows={3}
          value={damageNote}
          onChange={(e) => setDamageNote(e.target.value)}
          placeholder={t("Describe the damage")}
          className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </ConfirmModal>

      <Modal open={!!notesPart} setOpen={(open) => !open && setNotesPart(null)} width="max-w-3xl">
        <div className="space-y-2">
          <h1 className="text-lg font-semibold">{t("Notes")}</h1>
          <hr />
          {notesPart && (
            <NotesOutput
              notes={notesPart.internalNotes ?? []}
              resourceType={ResourceType.InternalPart}
              resourceId={notesPart.id}
              refetchQuery={["parts", notesPart.id]}
              shouldScroll={false}
            />
          )}
        </div>
      </Modal>

      {isLoadingAccounts ? (
        <LoadingWheel />
      ) : parts.length === 0 ? (
        <div className="text-sm text-gray-500">
          {nonRecyclingOnly
            ? t("No parts on a non-recycling flow for this vehicle.")
            : t("No parts found for this vehicle.")}
        </div>
      ) : (
        <div className="space-y-4">
          {parts.map((part) => {
            const isDamaged = part.partStatus === PartStatus.Damaged;
            const statusText = getPartStatusText(part.partStatus);

            return (
            <div
              key={part.id}
              className={classNames(
                "rounded-md border border-gray-200",
                // Amber when assigned to a non-recycling (customer) flow account — same cue as the jobs screen.
                isNonRecycleAccount(part) ? "bg-amber-100" : "bg-white"
              )}
            >
              <div
                className={classNames(
                  "grid grid-cols-1 gap-3 p-3",
                  isTMEEnabled && allowMarkDamaged ? "xl:grid-cols-7" : "xl:grid-cols-6"
                )}
              >
                <div className="xl:col-span-2">
                  <div className="text-sm font-semibold text-gray-900">{part.title ?? t("Untitled Part")}</div>
                  <div className="text-xs text-gray-500 mt-1">
                    {t("Tag: {{tag}}", { tag: part.tagNumber ?? "-" })}
                    {part.category?.name ? ` • ${part.category.name}` : ""}
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-600">{t("Status")}</div>
                  <div className="text-sm mt-1 text-gray-800">{statusText === "-" ? statusText : t(statusText)}</div>
                </div>

                <div>
                  <div className="text-xs font-semibold text-gray-600">{t("Grade")}</div>
                  <div className="mt-1">
                    <PartGradeBadge part={part} />
                  </div>
                </div>

                {isTMEEnabled && (
                  <div>
                    <div className="text-xs font-semibold text-gray-600">{t("Flow")}</div>
                    <select
                      className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
                      value={part.accountId ?? ""}
                      onChange={(e) => handleAccountChange(part, e.target.value)}
                      disabled={savingAccountForPartId === part.id}
                    >
                      <option value="">{t("Select account")}</option>
                      {accountOptions.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <div className="text-xs font-semibold text-gray-600">{t("Comment")}</div>
                  <button
                    type="button"
                    onClick={() => setNotesPart(part)}
                    className="mt-1 inline-flex items-center gap-x-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                  >
                    <PencilSquareIcon className="h-4 w-4 text-green-500" />
                    {t("Notes")} {part.internalNotes?.length ? `(${part.internalNotes.length})` : ""}
                  </button>
                </div>

                {allowMarkDamaged && (
                  <div className="flex items-center xl:justify-end">
                    <button
                      type="button"
                      onClick={() => openMarkDamaged(part)}
                      disabled={isDamaged}
                      className="inline-flex items-center gap-x-1 rounded-md border border-red-200 bg-white px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-400 disabled:hover:bg-white"
                    >
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      {isDamaged ? t("Damaged") : t("Mark Damaged")}
                    </button>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
